import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";

import { BannerComponent } from "@app/core/banner";
import { IExtendedBannerDTO, getBannersFromLocalStorage, getOffset, usePrevious, addFiltersToGTM } from "@app/util";
import { CtaSmallComponent } from "@app/core/cta-small/cta-small.component";
import { DealOverviewModule } from "@app/api/modules/deal-overview/deal-overview.module";
import { FIRST_DEAL_TAKE, DEAL_TAKE } from "@app/constants/overviews-numbers";
import { AnimatedEllipsisLoader } from "@app/core/animated-ellipsis-loader/animated-ellipsis-loader.component";
import { FilterBarDeals } from "@app/core/filter-bar";
import { DealCardWrapper } from "@app/components/deal-cards/deal-card-wrapper/deal-card-wrapper";
import { useAppSelector } from "@app/redux/store";
import { getAllFilters, getBanners, getDeals } from "@app/redux/thunks/deal-overview.thunk";
import { clearAllFilters, setCurrentTotalDeals } from "@app/redux/reducers/deals";
import { ViewType } from "@app/redux/reducers/settings";
import { IOutFeaturedDealDTO } from "@app/components/deal-cards/new-deal-card";
import { OverviewTabsWrapper } from "@app/components/overview-tabs/overview-tabs-wrapper";
import { useResizeObserver } from "@app/util/hooks/use-resize-observer";
import { useIntersection } from "@app/util/hooks/use-intersection";
import { MerchantSlider } from "@app/components/merchant-slider/merchant-slider";
import {
  allFiltersAndItemsAreEmpty,
  calculateTotalResultsWithCtaCards,
  getIfSticky,
  getOverviewItems,
  IOverviewItem,
  singleFiltersAreDifferent,
  useGetTotalHeight
} from "@app/util/overview-helpers";
import { CtaCard } from "@app/core/cta-card/cta-card";
import { FilterBarWrapper } from "@app/components/filter-bar-wrapper/filter-bar-wrapper";

import styles from "./deal-overview-component.module.scss";

export interface IDealOverviewComponentProps {
  dealOverviewModule: DealOverviewModule;
}

let previousSkip: undefined | number;

const DealOverview = (props: IDealOverviewComponentProps) => {
  const dispatch = useDispatch();
  const { screenSize, platformId } = useAppSelector((state) => state.settings);
  const {
    categoryFilterItems,
    brandFilterItems,
    merchantFilterItems,
    deals,
    totalResults,
    orderByValues,
    sortBy,
    banners: rdxBanners
  } = useAppSelector((state) => state.dealOverview);

  const { merchants } = useAppSelector((state) => state.merchantOverview);

  const { overviewMessages, ctaCards } = props.dealOverviewModule;
  const prevOrderBy = usePrevious(orderByValues);
  const mainDivRef = useRef<HTMLDivElement>(null);
  const totalResultsWithCtaCards = calculateTotalResultsWithCtaCards(totalResults, ctaCards);

  const isFirstRender = useRef(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [progressPage, setProgressPage] = useState<number>(1);
  const [overviewItems, setOverviewItems] = useState<IOverviewItem<IOutFeaturedDealDTO>[] | undefined>(undefined);
  const [banners, setBanners] = useState<IExtendedBannerDTO[]>([]);
  const [listRef, { height: listRefHeight }] = useResizeObserver<HTMLDivElement>();
  const { ref: bottomRef, entry: bottomEntry } = useIntersection({ threshold: 1 });
  const { ref: filterBarRef, entry: filterBarEntry } = useIntersection({ threshold: 1 });
  const isSticky = getIfSticky(filterBarEntry, bottomEntry, totalResults, deals.length);
  const viewType = screenSize ? screenSize.viewType : ViewType.Desktop;
  const overviewItemsToUse = overviewItems || getOverviewItems(viewType, deals, [], merchants, ctaCards);
  const totalHeight = useGetTotalHeight(overviewItemsToUse.length, totalResultsWithCtaCards, listRefHeight);

  const [rerenderOverview, setRerenderOverview] = useState<boolean>(false);

  useEffect(() => {
    window.scroll(0, 0);
    setInitialValues();

    if (!deals || deals.length === 0) {
      dispatch(
        getDeals(
          0,
          FIRST_DEAL_TAKE,
          categoryFilterItems,
          brandFilterItems,
          merchantFilterItems,
          platformId,
          orderByValues ? orderByValues[1] : undefined,
          orderByValues ? orderByValues[0] : undefined
        )
      );
    }

    isFirstRender.current = false;

    if (viewType !== ViewType.Desktop) {
      // If the viewType is not desktop, we want to rerender the overview items
      // to ensure that the correct items are displayed based on the current viewType.
      // Otherwise the render will not update correctly when the viewType changes.
      setRerenderOverview(true);
    }
  }, []);

  useEffect(() => {
    if (bottomEntry?.isIntersecting && !isFetching) {
      bottomPageCallback();
    }
  }, [bottomEntry]);

  useEffect(() => {
    if (
      filtersAreDifferent() ||
      allFiltersAndItemsAreEmpty([categoryFilterItems, brandFilterItems, merchantFilterItems], deals)
    ) {
      setProgressPage(1);
      previousSkip = 0;
      const allFiltersCombined = [
        ...categoryFilterItems.filter((item) => item.isSelected),
        ...brandFilterItems.filter((item) => item.isSelected),
        ...merchantFilterItems.filter((item) => item.isSelected)
      ];
      addFiltersToGTM(allFiltersCombined);
      dispatch(getBanners(platformId, categoryFilterItems, brandFilterItems));

      if (!isFirstRender) dispatch(setCurrentTotalDeals(0));

      const actualScrollPosition = getActualScrollPosition();
      if (mainDivRef?.current && actualScrollPosition > 0) {
        window.scroll(0, 0);
      }
    }
  }, [brandFilterItems, categoryFilterItems, merchantFilterItems, sortBy]);

  useEffect(() => {
    if (prevOrderBy !== null) {
      setProgressPage(1);
      previousSkip = 0;

      const actualScrollPosition = getActualScrollPosition();
      if (mainDivRef?.current && actualScrollPosition > 0) {
        window.scroll(0, 0);
      }

      dispatch(getBanners(platformId, categoryFilterItems, brandFilterItems));
    }
  }, [orderByValues]);

  useEffect(() => {
    if (deals.length > 0) {
      const overviewItemsResult = getOverviewItems(viewType, deals, banners, merchants, ctaCards);
      setOverviewItems(overviewItemsResult);
    } else {
      setIsLoading(false);
    }
  }, [deals, screenSize, banners, merchants]);

  useEffect(() => {
    const extendedBanners: IExtendedBannerDTO[] = getBannersFromLocalStorage(rdxBanners);

    setBanners(extendedBanners);
  }, [rdxBanners]);

  const bottomPageCallback = async () => {
    const newSkip = FIRST_DEAL_TAKE + (progressPage - 1) * DEAL_TAKE;
    if (deals.length < totalResults && !isFirstRender.current && previousSkip !== newSkip) {
      // totalcurrentmerchants is async and will sometimes give the wrong amount;
      // const skip = props.totalCurrentDeals || 0;
      previousSkip = newSkip;
      setIsLoading(true);
      setProgressPage(progressPage + 1);
      // Type is wrong, we can await this.
      await dispatch(
        getDeals(
          newSkip,
          DEAL_TAKE,
          categoryFilterItems,
          brandFilterItems,
          merchantFilterItems,
          platformId,
          orderByValues ? orderByValues[1] : undefined,
          orderByValues ? orderByValues[0] : undefined
        )
      );
      setIsFetching(false);
    } else {
      setIsLoading(false);
      setIsFetching(false);
    }
  };

  const prevCategoryFilterItems = usePrevious(categoryFilterItems);
  const prevBrandFilterItems = usePrevious(brandFilterItems);
  const prevMerchantFilterItems = usePrevious(merchantFilterItems);
  const filtersAreDifferent = (): boolean => {
    if (!prevCategoryFilterItems && !prevBrandFilterItems && !prevMerchantFilterItems) {
      return false;
    }

    const categoryFiltersAreDifferent = singleFiltersAreDifferent(prevCategoryFilterItems || [], categoryFilterItems);
    const brandFiltersAreDifferent = singleFiltersAreDifferent(prevBrandFilterItems || [], brandFilterItems);
    const merchantFiltersAreDifferent = singleFiltersAreDifferent(prevMerchantFilterItems || [], merchantFilterItems);

    return categoryFiltersAreDifferent || brandFiltersAreDifferent || merchantFiltersAreDifferent;
  };

  const getActualScrollPosition = () => {
    const position = getOffset(mainDivRef.current);
    const currentScrollPosition = document.body.scrollTop || document.documentElement.scrollTop;
    const actualScrollPosition = currentScrollPosition - position;

    return actualScrollPosition;
  };

  const setInitialValues = () => {
    if (allFiltersAndItemsAreEmpty([categoryFilterItems, brandFilterItems, merchantFilterItems], deals, true)) {
      dispatch(getAllFilters(platformId));
    }

    dispatch(getBanners(platformId));
  };

  return (
    <div>
      <OverviewTabsWrapper className={styles.tabs} tabs={props.dealOverviewModule.tabs} />
      <FilterBarWrapper
        mainDivRef={mainDivRef}
        filterBarRef={filterBarRef}
        isSticky={isSticky}
        totalHeight={totalHeight}
        platform={props.dealOverviewModule.platform}
      >
        <FilterBarDeals />
      </FilterBarWrapper>
      <div className={styles.overview}>
        <div className="uk-container">
          {overviewMessages.onTop.isActive && (
            <div className={styles.noBF}>
              <h2>{overviewMessages.onTop.message}</h2>
            </div>
          )}
          {deals && deals.length > 0 ? (
            <div ref={listRef} className={styles.grid}>
              {overviewItemsToUse.map((overviewItem) => {
                const { id, item, banner, ctaCard, merchantSlider } = overviewItem;

                return (
                  <React.Fragment key={`${id}-${rerenderOverview}`}>
                    {item && <DealCardWrapper deal={item} />}

                    {ctaCard && (
                      <CtaCard
                        title={ctaCard.title}
                        subtitle={ctaCard.subtitle}
                        href={ctaCard.link?.url || ""}
                        openInNewTab={ctaCard.link?.target === "_blank"}
                      />
                    )}

                    {banner && (
                      <div className={styles.banner}>
                        <BannerComponent
                          banner={banner}
                          showAlternativeBanner={banner.showAlternativeBanner}
                          variant="default"
                        />
                      </div>
                    )}
                    {merchantSlider?.merchants && merchantSlider.merchants.length > 0 && (
                      <MerchantSlider
                        className={styles.merchantSlider}
                        merchants={merchantSlider.merchants}
                        startIndex={merchantSlider.startIndex}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          ) : !allFiltersAndItemsAreEmpty([categoryFilterItems, brandFilterItems], deals) && !isLoading ? (
            <div>
              <CtaSmallComponent
                buttonTitle={overviewMessages.noResultsWithFilter.buttonText}
                onClick={() => dispatch(clearAllFilters())}
                ctaText={overviewMessages.noResultsWithFilter.text}
                icon={props.dealOverviewModule.emptyStateIcon}
              />
            </div>
          ) : (
            !isLoading &&
            overviewMessages.emptyResults.isActive && (
              <div className={styles.noBF}>
                <h2>{overviewMessages.emptyResults.message}</h2>
              </div>
            )
          )}
          {isLoading && (
            <div className={styles.loadingSpinner}>
              <AnimatedEllipsisLoader />
            </div>
          )}
        </div>
      </div>
      <div ref={bottomRef} className={styles.bottomRef} />
    </div>
  );
};

export { DealOverview };
